import React, { useEffect, useMemo } from 'react';
import './SubmissionView.css';
import './IdePage.css';
import { FINAL_PROBLEM_ID, PROBLEMS } from './problems';
import IDE from './IDE';

const safeParse = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
};

const loadSubmissionSnapshot = () => {
  const problemId = FINAL_PROBLEM_ID;
  const steps = safeParse(`rt_${problemId}_steps`, null) ?? safeParse('rt_steps', []);

  let code = '';
  try {
    const raw = localStorage.getItem('rt_ide_code');
    if (typeof raw === 'string') code = raw;
  } catch {
    code = '';
  }
  if (!code || code === 'null' || code === 'undefined') {
    code = PROBLEMS[problemId]?.starterCode || '';
  }

  return {
    problemId,
    problemTitle: PROBLEMS[problemId]?.title || 'Your submission',
    problemDescription: PROBLEMS[problemId]?.description || '',
    steps: Array.isArray(steps) ? steps : [],
    code,
  };
};

const StepsList = ({ steps }) => {
  if (!steps?.length) {
    return (
      <div className="submission-empty-card">
        <p>No saved steps were found.</p>
        <p className="submission-muted">Finish the task to see your notes here.</p>
      </div>
    );
  }

  const renderStep = (step, idx) => {
    const indentPx = Math.max(0, Number(step?.indent ?? 0)) * 50;

    return (
      <div key={step?.id ?? idx} className="draggable-wrapper">
        <div className="step-row">
          <div className="indent-spacer" style={{ width: `${indentPx}px` }}>
            {indentPx > 0 && (
              <div className="substep-indicator">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 4V14C10 14.5304 10.2107 15.0391 10.5858 15.4142C10.9609 15.7893 11.4696 16 12 16H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M15 11L20 16L15 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            )}
          </div>
          <div
            className={`step-container ${step?.isCorrect === true ? 'correct' : ''} ${step?.isCorrect === false ? 'incorrect' : ''}`}
          >
            <div className="description-wrapper">
              <textarea
                className="description-box"
                value={step?.description ?? ''}
                placeholder="Describe your step in natural language..."
                readOnly
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="submission-steps-list">
      {steps.map((step, idx) => renderStep(step, idx))}
    </div>
  );
};

export function SubmissionTaskPage() {
  const snapshot = useMemo(() => loadSubmissionSnapshot(), []);
  const problem = PROBLEMS[snapshot.problemId] || {};

  useEffect(() => {
    try {
      localStorage.setItem('rt_last_route', '/submission/task');
      localStorage.setItem('rt_flow_stage', 'done');
    } catch {}
  }, []);

  return (
    <div className="submission-view-page app-container submission-readonly">
      <header>
        <h1>RunThrough</h1>
        <div className="problem-title">
          <h3>{problem.title}</h3>
          <p>{snapshot.problemDescription}</p>
        </div>
      </header>

      <main>
        <StepsList steps={snapshot.steps} />
      </main>
    </div>
  );
}

export function SubmissionCodePage() {
  const snapshot = useMemo(() => loadSubmissionSnapshot(), []);
  const problem = PROBLEMS[snapshot.problemId] || {};

  useEffect(() => {
    try {
      localStorage.setItem('rt_last_route', '/submission/code');
      localStorage.setItem('rt_flow_stage', 'done');
    } catch {}
  }, []);

  return (
    <div className="submission-view-page submission-readonly submission-code-only">
      <header>
        <h1>RunThrough</h1>
        <div className="problem-title">
          <h3>{problem.title || 'Your submission'}</h3>
          <p className="submission-muted">Read-only view of your submitted IDE code.</p>
        </div>
      </header>

      <div className="ide-only-shell">
        <IDE
          value={snapshot.code || '# No code submission found.'}
          onChange={() => {}}
          onSubmit={() => {}}
          showInlineSubmit={false}
          readOnly
        />
      </div>
    </div>
  );
}
